/**
 * Drivers API Service
 */

import api from './api';
import type {
  ApiResponse,
  RoutingDriver,
  DriverWithPosition,
  TrackPoint,
  AvailableDriver,
  DriverSchedule,
  AvailabilityCalendar,
} from '../types';

/**
 * Get all drivers
 */
export async function getDrivers(): Promise<RoutingDriver[]> {
  const response = await api.get<ApiResponse<RoutingDriver[]>>('/drivers');
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch drivers');
  }
  
  return response.data.data;
}

/**
 * Get available drivers for a date/time
 */
export async function getAvailableDrivers(
  cityId: number,
  date?: string
): Promise<AvailableDriver[]> {
  const queryParams = new URLSearchParams();
  queryParams.set('cityId', cityId.toString());
  if (date) queryParams.set('date', date);

  const response = await api.get<ApiResponse<AvailableDriver[]>>(
    `/drivers/available?${queryParams.toString()}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch available drivers');
  }
  
  return response.data.data;
}

/**
 * Get driver details with schedule
 */
export async function getDriver(driverUid: string): Promise<RoutingDriver & { schedule?: DriverSchedule }> {
  const response = await api.get<ApiResponse<RoutingDriver & { schedule?: DriverSchedule }>>(
    `/drivers/${driverUid}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch driver');
  }
  
  return response.data.data;
}

/**
 * Get driver availability calendar
 */
export async function getDriverAvailability(
  driverUid: string,
  startDate?: string,
  endDate?: string
): Promise<AvailabilityCalendar> {
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.set('startDate', startDate);
  if (endDate) queryParams.set('endDate', endDate);

  const response = await api.get<ApiResponse<AvailabilityCalendar>>(
    `/drivers/${driverUid}/availability?${queryParams.toString()}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch driver availability');
  }
  
  return response.data.data;
}

/**
 * Check if driver is available at a specific time
 */
export async function isDriverAvailable(
  driverUid: string,
  date?: string
): Promise<{ driverUid: string; date: string; isAvailable: boolean }> {
  const queryParams = date ? `?date=${date}` : '';
  const response = await api.get<ApiResponse<{ driverUid: string; date: string; isAvailable: boolean }>>(
    `/drivers/${driverUid}/is-available${queryParams}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to check driver availability');
  }
  
  return response.data.data;
}

/**
 * Create a new driver
 */
export async function createDriver(data: {
  first_name: string;
  last_name: string;
  phone: string;
}): Promise<{ uid: string; first_name: string; last_name: string; phone: string }> {
  const response = await api.post<ApiResponse<{ uid: string; first_name: string; last_name: string; phone: string }>>(
    '/drivers',
    data
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to create driver');
  }

  return response.data.data;
}

/**
 * Toggle driver verification status
 */
export async function toggleDriverVerification(
  driverUid: string,
  verified: boolean,
  reason: string
): Promise<{ uid: string; is_verified: boolean; message: string }> {
  const response = await api.post<ApiResponse<{ uid: string; is_verified: boolean; message: string }>>(
    `/drivers/${driverUid}/toggle-verification`,
    { verified, reason }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to toggle driver verification');
  }

  return response.data.data;
}

/**
 * Get live driver positions
 */
export async function getLiveDriverPositions(
  cityId: number,
  maxAgeMinutes = 30
): Promise<DriverWithPosition[]> {
  const response = await api.get<ApiResponse<DriverWithPosition[]>>(
    `/drivers/positions/live?cityId=${cityId}&maxAgeMinutes=${maxAgeMinutes}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch driver positions');
  }
  
  return response.data.data;
}

/**
 * One driver's background-GPS path since `since` (at most 24 h), oldest point first
 */
export async function getDriverTrack(driverUid: string, since: Date): Promise<TrackPoint[]> {
  const response = await api.get<ApiResponse<TrackPoint[]>>(
    `/driver-positions/${encodeURIComponent(driverUid)}/track`,
    { params: { startDate: since.toISOString() } }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch driver path');
  }

  return response.data.data;
}
